import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
// Clarity grades with visual indicators (dots represent inclusions)
const CLARITY_CONFIG = {
    'FL': { dots: 0, color: '#10b981', label: 'Flawless' },
    'IF': { dots: 0, color: '#34d399', label: 'Internally Flawless' },
    'VVS1': { dots: 1, color: '#3b82f6', label: 'Very Very Slightly Included' },
    'VVS2': { dots: 2, color: '#60a5fa', label: 'Very Very Slightly Included' },
    'VS1': { dots: 3, color: '#8b5cf6', label: 'Very Slightly Included' },
    'VS2': { dots: 4, color: '#a78bfa', label: 'Very Slightly Included' },
    'SI1': { dots: 5, color: '#f59e0b', label: 'Slightly Included' },
    'SI2': { dots: 6, color: '#fbbf24', label: 'Slightly Included' },
    'SI3': { dots: 7, color: '#f97316', label: 'Slightly Included' },
    'I1': { dots: 8, color: '#ef4444', label: 'Included' },
    'I2': { dots: 9, color: '#dc2626', label: 'Included' },
    'I3': { dots: 10, color: '#b91c1c', label: 'Included' },
};
const ClarityFilterContainer = styled.div `
  margin-bottom: 20px;
`;
const FilterSectionTitle = styled.h4 `
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
  margin-top: 0;
`;
const ClarityScale = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(58px, 1fr));
  gap: 6px;
`;
const ClarityDots = styled.div `
  display: flex;
  gap: 2px;
  justify-content: center;
  margin-bottom: 4px;
  height: 10px;
  align-items: center;

  .dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${props => props.$dotColor};
    opacity: 0.7;
    transition: all 0.2s ease;
  }

  .dot.empty {
    background: transparent;
    border: 1px solid ${props => props.$dotColor};
    opacity: 0.3;
  }
`;
const ClarityPill = styled.button `
  padding: 8px 10px;
  min-width: 58px;
  background: ${props => props.$disabled ? 'var(--bg-tertiary)' :
    props.$isActive ? 'var(--accent-gold-bg, rgba(153, 101, 21, 0.08))' : 'var(--bg-secondary)'};
  border: 1px solid ${props => props.$disabled ? 'var(--border-color)' :
    props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--border-color)'};
  color: ${props => props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--text-secondary)'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  opacity: ${props => props.$disabled ? 0.4 : 1};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  &:hover {
    border-color: ${props => props.$disabled ? 'var(--border-color)' : 'var(--accent-gold-border, rgba(153, 101, 21, 0.25))'};
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$disabled ? 'none' : '0 4px 8px rgba(0,0,0,0.1)'};
  }

  &:hover ${ClarityDots} .dot {
    opacity: 1;
    transform: scale(1.2);
  }

  .clarity-label {
    font-weight: 600;
    font-size: 12px;
  }

  .clarity-count {
    font-size: 10px;
    color: var(--text-tertiary);
  }
`;
const AllClaritiesPill = styled(ClarityPill) `
  background: ${props => props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--bg-secondary)'};
  color: ${props => props.$isActive ? '#fff' : 'var(--text-primary)'};
  font-weight: 600;
  min-width: 65px;

  .clarity-label {
    color: ${props => props.$isActive ? '#fff' : 'var(--text-primary)'};
  }

  .clarity-count {
    color: ${props => props.$isActive ? 'rgba(255,255,255,0.8)' : 'var(--text-tertiary)'};
  }
`;
export const ClarityFilter = ({ selectedClarity, clarities, onClarityChange, getClarityCount, clarityQuality }) => {
    // Ensure selected clarity is in the list even if it has 0 results
    const displayClarities = selectedClarity && !clarities.includes(selectedClarity)
        ? [...clarities, selectedClarity].sort()
        : clarities;
    const totalCount = displayClarities.reduce((sum, clarity) => sum + getClarityCount(clarity), 0);
    const renderDots = (clarity) => {
        const config = CLARITY_CONFIG[clarity];
        if (!config)
            return null;
        const maxDots = 5;
        const filledDots = Math.min(config.dots, maxDots);
        return (_jsx(ClarityDots, { "$dotColor": config.color, children: [...Array(maxDots)].map((_, i) => (_jsx("span", { className: `dot ${i < filledDots ? '' : 'empty'}` }, i))) }));
    };
    return (_jsxs(ClarityFilterContainer, { children: [_jsx(FilterSectionTitle, { children: "Diamond Clarity" }), _jsxs(ClarityScale, { children: [_jsxs(AllClaritiesPill, { "$isActive": !selectedClarity, onClick: () => onClarityChange(''), title: `All Clarities (${totalCount} diamonds)`, children: [_jsx("span", { className: "clarity-label", children: "All" }), _jsx("span", { className: "clarity-count", children: totalCount })] }), displayClarities.map(clarity => {
                        const count = getClarityCount(clarity);
                        const isDisabled = count === 0;
                        const config = CLARITY_CONFIG[clarity];
                        const isFromAISearch = !clarities.includes(clarity) && selectedClarity === clarity;
                        return (_jsxs(ClarityPill, { "$isActive": selectedClarity === clarity, "$disabled": isDisabled, "$clarityColor": config?.color, onClick: () => onClarityChange(isDisabled ? '' : clarity), title: isFromAISearch
                                ? `${clarity} (0 diamonds - from AI search, click to clear)`
                                : `${clarity} - ${config?.label || ''} (${count} diamonds)`, children: [renderDots(clarity), _jsx("span", { className: "clarity-label", children: clarity }), _jsx("span", { className: "clarity-count", children: count })] }, clarity));
                    })] })] }));
};
